import { BaseMenu, BaseMenuDivider, BaseMenuItem } from '@components/BaseMenu';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import type { MediaActionBarModel } from '@lib/media/mediaActionBar';
import type {
  MediaActionControl,
  MediaMenuAction,
  MediaMenuHostItem,
} from '@lib/media/mediaActions';
import { isMediaVerbId, mediaOutcome } from '@lib/verbs';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton, Tooltip } from '@mui/material';
import { MobileMediaMenuDrawer } from '@pages/Library/components/Mobile/MobileMediaMenuDrawer';
import { trackingStore } from '@stores/Tracking';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import { MouseEvent, ReactElement, useState } from 'react';
import css from './MediaActionBar.module.scss';
import { getMediaActionIcon } from './mediaActionIcons';

/**
 * The one verb a host may narrow to a range.
 *
 * Named once because three things have to agree on it: whether the accented pill shows the
 * range, whether a menu row shows it instead, and which id `onWorkWithAgent` intercepts. They
 * were three separate string literals in three files, which is how the cue and the routing
 * could drift apart.
 */
const SCOPED_VERB: MediaMenuAction = 'work-with-agent';

/**
 * What the agent verb has been narrowed to: `label` is the range itself ("0:42 – 1:05"),
 * `description` the sentence behind it ("Opens a new chat about 0:42 – 1:05 of this media").
 *
 * One object rather than two strings so the two cannot be passed half and drift apart — they
 * are one fact said at two lengths. Exported rather than redeclared by each wrapper: a
 * structural literal repeated in three files still typechecks when an OPTIONAL field is added
 * to one of them, and the field then exists on the bar while neither wrapper can forward it —
 * which is exactly how `iconVariant` ended up declared on the bar and reachable from nowhere.
 */
export interface AgentScope {
  label: string;
  description: string;
}

/**
 * How much of the bar to render, and how wide.
 *
 * Shared with both wrappers (and, through them, with `MomentModal`'s `actionBar` helper) rather
 * than redeclared in each — four copies of this trio is how `iconVariant` ended up declared on
 * the bar and reachable from nowhere.
 */
export interface MediaActionBarLayoutProps {
  /**
   * `both` (default) — the pills and the ⋯ side by side, which is the desktop header.
   *
   * `pills` / `kebab` split the bar across two mount points. The mobile modal needs that: its
   * `.mobilePanelHeader` row is already measured to overflow at 320px, so the pills go in the
   * modal's sticky footer while the ⋯ stays in that row.
   *
   * One prop rather than two booleans: `pillsOnly` and `kebabOnly` would admit a contradictory
   * fourth state that silently resolved in favour of one of them.
   */
  parts?: 'both' | 'pills' | 'kebab';
  /**
   * Stretch the pills to fill their container — the mobile modal's sticky footer. They share
   * one row, compacting to fit down to a 320px phone, and stack only when even that can't hold
   * both labels — see `.barFullWidth`.
   */
  fullWidth?: boolean;
  className?: string;
}

interface MediaActionBarProps extends MediaActionBarModel, MediaActionBarLayoutProps {
  media: Media;
  /**
   * `control` says WHICH of this bar's two halves ran the action, and it is this component's
   * call rather than the caller's — only it knows whether the click came from the accented
   * pill, the desktop kebab or the mobile drawer.
   *
   * Load-bearing for the change that introduced this bar: the whole question the ticket raised
   * is whether burying the verbs costs their usage, and that is unanswerable if a CTA click and
   * a kebab row report the same control. An earlier draft passed one value for both.
   */
  onAction: (id: MediaMenuAction, control: MediaActionControl) => void;
  /**
   * What a click on the accented pill reports as. Menu rows report `'media-kebab'` /
   * `'media-drawer'` and need no prop — those names are true on every surface; the pills' are
   * not, so the caller names them.
   */
  ctaControl: MediaActionControl;
  /** What a click on the unaccented pill beside it reports as. See `ctaControl`. */
  secondaryControl: MediaActionControl;
  /**
   * Fired synchronously on each closed→open transition, BEFORE the menu opens.
   *
   * This is how the "Copy link at 1:30" row gets its timestamp. The caller owns the action
   * hook, so it is the caller that must freeze the playhead — see `useFrozenPlayhead`. Because
   * both state updates land in the same React batch, the menu's first render already carries
   * the row.
   *
   * A host with no video player (grid card, table row) simply omits this.
   */
  onMenuOpen?: () => void;
  /**
   * The range the agent verb has been narrowed to, when the host has narrowed it.
   *
   * Shown wherever that verb renders, NOT wherever the accented pill is. The two are the same
   * control in an ordinary library, so the distinction is invisible there — but in a
   * transcript-only one the pill is the clip verb (`splitMediaActions` decides, not this
   * component) and the agent verb drops into the ⋯ menu and the mobile drawer. Gating this on
   * CTA-ness meant the range silently changed what a menu row did with nothing on screen
   * saying so; the clip verb still shows no range, because the range does not apply to it.
   *
   * On the pill it rides beside the label rather than replacing it, in tabular numerals, so the
   * button's identity never changes under the cursor and the digits do not reflow while a
   * handle is being dragged — only crossing an hour or a tenth of the media's length moves it,
   * and the pill is `nowrap`.
   *
   * The secondary pill beside a scoped agent CTA says, in its tooltip, that it does NOT narrow:
   * clipping always takes the whole media, and two pills side by side read as acting on the
   * same thing unless one of them says otherwise.
   */
  agentScope?: AgentScope;
  /**
   * Overrides what the agent verb DOES, without changing what it says.
   *
   * Lives here rather than in each wrapper's `onAction` because it was written twice, verbatim,
   * once per bar — and each copy carried in prose the obligation to report the click itself.
   * A third bar would have written it a third time. Routed for every control, so the pill, the
   * ⋯ row and the drawer row cannot end up with different targets.
   *
   * The override owns the click's analytics: this path deliberately does not reach `onAction`,
   * and `onAction` is what reaches the dispatcher that fires `Media Menu Action`. The `control`
   * is handed over for exactly that reason — reporting one hardcoded value for all three is the
   * conflation `ctaControl` exists to avoid.
   */
  onWorkWithAgent?: (control: MediaActionControl) => void;
  /**
   * Rows only the host can offer, appended below the housekeeping group.
   *
   * The moment modal's "Download .srt" is the one that asked for this: it needs the transcript
   * the modal has already fetched, so it cannot come from the hooks that build the rest of the
   * menu — see `MediaMenuHostItem`.
   *
   * Last, not first: the host's rows are extras, and putting them above the verbs would move
   * the CTA's siblings down the menu on one surface only.
   */
  hostItems?: MediaMenuHostItem[];
}

/**
 * One accented CTA plus one ⋯ menu — the replacement for the three-glyph verb rail beside a
 * kebab. Clipping rides beside an agent CTA as a quieter, unaccented pill (`secondary`), so the
 * Clipper is one click away without spending the screen's one accent on a second button.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PRESENTATIONAL ONLY. No action hooks here — that is what makes it route-safe.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It takes an already-split `MediaActionBarModel` (see `splitMediaActions`) rather than calling
 * a hook, so the same component serves the library page — where rename/retry/delete exist — and
 * `/search` and `/atlas`, where they cannot. The callers differ only in which hook they feed it
 * from: `LibraryMediaActionBar` uses `useMediaMenuActions` (both halves), `MediaVerbsActionBar`
 * uses `useMediaVerbs`.
 *
 * Why one button and not three: three glyphs plus a kebab was reported as too many icons, and
 * an icon rail teaches nothing on a surface that has no labelled counterpart. One button that
 * says what it does, with the rest a click away, is the trade — the ticket's words were "an
 * obvious button that is a button and not a tag".
 *
 * The CTA can be `null` (still-processing media). That is the one place this component departs
 * from the app's "dim it, never hide it" rule, and it's deliberate: the verbs are still in the
 * kebab, dimmed, with their reasons, so nothing becomes unlearnable — see `splitMediaActions`.
 */
export const MediaActionBar = ({
  media,
  cta,
  secondary,
  verbItems,
  settingsItems,
  onAction,
  ctaControl,
  secondaryControl,
  onMenuOpen,
  agentScope,
  onWorkWithAgent,
  hostItems,
  parts = 'both',
  fullWidth,
  className,
}: MediaActionBarProps) => {
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    // Before opening, so the caller's frozen playhead is in place for the render that shows
    // the menu. Both state updates batch, so the timestamped row is there on the first frame.
    onMenuOpen?.();
    if (isMobile) {
      setDrawerOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
    trackingStore.mediaMenuOpened({ mediaId: media._id, libraryId: media.libraryId });
  };

  const handleClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    trackingStore.mediaMenuClosed({ mediaId: media._id, libraryId: media.libraryId });
  };

  // Every control goes through here, so a host that narrows the agent verb narrows it on all
  // three of them at once.
  const dispatch = (id: MediaMenuAction, control: MediaActionControl) => {
    if (id === SCOPED_VERB && onWorkWithAgent) {
      onWorkWithAgent(control);
      return;
    }
    onAction(id, control);
  };

  const handleMenuAction = (id: MediaMenuAction) => (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    dispatch(id, 'media-kebab');
  };

  // Resolved once, so the pill's badge and its tooltip cannot end up gated differently.
  const scope = cta?.id === SCOPED_VERB ? agentScope : undefined;

  // ...and when the pill is some other verb, the range follows the agent verb into the menu
  // and the drawer instead of going unmentioned. One derived array feeds both, which is what
  // keeps the two presentations from drifting (`.claude/rules/context-menu-actions.md`).
  const scopedVerbItems =
    agentScope && !scope
      ? verbItems.map((item) =>
          item.id === SCOPED_VERB
            ? { ...item, label: `${item.label} · ${agentScope.label}` }
            : item,
        )
      : verbItems;

  const ctaButton = cta ? (
    <button
      type="button"
      className={classNames(css.cta, fullWidth && css.pillFullWidth)}
      onClick={(event) => {
        event.stopPropagation();
        dispatch(cta.id, ctaControl);
      }}
    >
      <span className={css.ctaIcon}>{getMediaActionIcon(cta.id, media)}</span>
      <span className={css.pillLabel}>{cta.label}</span>
      {scope && <span className={css.ctaScope}>{scope.label}</span>}
    </button>
  ) : null;

  // Each pill says what its click produces — the label names the action, not where it lands.
  // A narrowed agent verb names its range instead, and the pill beside it says it does NOT
  // narrow: two pills side by side otherwise read as acting on the same thing.
  const outcomeOf = (item: MediaActionBarModel['cta'], ignoredRangeLabel?: string) =>
    item && isMediaVerbId(item.id) ? mediaOutcome(item.id, ignoredRangeLabel) : undefined;
  const ctaTooltip = scope ? scope.description : outcomeOf(cta);
  const secondaryTooltip = outcomeOf(secondary, scope?.label);

  const withTooltip = (button: ReactElement | null, title: string | undefined) =>
    button && title ? (
      <Tooltip title={title} arrow placement="bottom">
        {button}
      </Tooltip>
    ) : (
      button
    );

  const secondaryButton = secondary ? (
    <button
      type="button"
      className={classNames(css.secondary, fullWidth && css.pillFullWidth)}
      onClick={(event) => {
        event.stopPropagation();
        dispatch(secondary.id, secondaryControl);
      }}
    >
      <span className={css.secondaryIcon}>{getMediaActionIcon(secondary.id, media)}</span>
      <span className={css.pillLabel}>{secondary.label}</span>
    </button>
  ) : null;

  // Nothing at all when this instance is the pills half and there are no pills — a
  // still-processing media must not leave the mobile modal's footer as an empty bar.
  // `secondary` is never set without `cta`, so the CTA alone decides.
  //
  // This alone does NOT hide that bar: `MobileModal`'s `{footer && …}` sees a React element,
  // which is truthy however it renders, so the wrapper and its border still paint. The
  // `:empty` rule in `MobileModal.module.scss` is what actually hides it. Returning `null`
  // here is what makes that rule apply — the two work together, so don't delete either as
  // redundant.
  if (parts === 'pills' && !cta) return null;

  const kebab = (
    <>
      <Tooltip title="More options" arrow placement="top">
        <IconButton
          className={css.kebab}
          onClick={handleOpen}
          aria-label="media menu"
          aria-controls={open ? 'media-action-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <MoreHorizIcon />
        </IconButton>
      </Tooltip>

      {isMobile ? (
        <MobileMediaMenuDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            trackingStore.mediaMenuClosed({ mediaId: media._id, libraryId: media.libraryId });
          }}
          onOpen={() => setDrawerOpen(true)}
          media={media}
          groups={[scopedVerbItems, settingsItems]}
          hostItems={hostItems}
          onAction={(id) => dispatch(id, 'media-drawer')}
        />
      ) : (
        <BaseMenu id="media-action-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
          {scopedVerbItems.map((item) => (
            <BaseMenuItem
              key={item.id}
              icon={getMediaActionIcon(item.id, media)}
              onClick={handleMenuAction(item.id)}
              disabled={item.disabled}
              disabledReason={item.disabledReason}
            >
              {item.label}
            </BaseMenuItem>
          ))}
          {/* No "Media settings" heading. The group above it is verbs, so a title naming the
              whole menu would be contradicted by its own top half — the hairline separates the
              two categories quietly instead. `VideoMenuButton` still shows the heading, because
              its menu has no verb group to separate from. */}
          {scopedVerbItems.length > 0 && settingsItems.length > 0 && <BaseMenuDivider />}
          {settingsItems.map((item) => (
            <BaseMenuItem
              key={item.id}
              icon={getMediaActionIcon(item.id, media)}
              onClick={handleMenuAction(item.id)}
              disabled={item.disabled}
              disabledReason={item.disabledReason}
              danger={item.danger}
            >
              {item.label}
            </BaseMenuItem>
          ))}
          {/* The host's own rows, behind their own hairline so they read as a third category
              rather than more housekeeping. */}
          {hostItems && hostItems.length > 0 && <BaseMenuDivider />}
          {hostItems?.map((item) => (
            <BaseMenuItem
              key={item.id}
              icon={item.icon}
              onClick={(event) => {
                event.stopPropagation();
                setAnchorEl(null);
                item.onSelect();
              }}
            >
              {item.label}
            </BaseMenuItem>
          ))}
        </BaseMenu>
      )}
    </>
  );

  return (
    <div className={classNames(css.bar, fullWidth && css.barFullWidth, className)}>
      {parts !== 'kebab' && withTooltip(secondaryButton, secondaryTooltip)}
      {parts !== 'kebab' && withTooltip(ctaButton, ctaTooltip)}
      {parts !== 'pills' && kebab}
    </div>
  );
};
